/**
 * AP-11 — PEAK-artiges TRC-Format, portiert aus `src/trace/TrcWriter.cpp` und
 * `src/trace/TrcReader.cpp`.
 *
 * Zeilenform: `<nr>)  <ms>  <Rx|Tx> <id>  <dlc> <byte>...`, Kommentare mit `;`.
 * Anders als bei ASC gibt es hier kein `x`-Suffix: 11-Bit-IDs werden mit 3,
 * 29-Bit-IDs mit 8 Hexstellen geschrieben, der Leser entscheidet über die
 * Tokenlänge (`idToken.size() > 3`) — das ist im Original bereits stimmig.
 */

import type { CanFrame } from '../can/ICanAdapter';
import {
  clampDlc,
  formatDataBytes,
  parseDataBytes,
  parseHex,
  type TraceWriter,
} from './types';

const TRC_HEADER =
  ';##########################################################################\n' +
  ';   CAN_MON_Cpp_FULL TRC-style trace file (best-effort format, see README.md)\n' +
  ';   Message Number, Time Offset (ms), Type, ID (hex), Length, Data (hex)\n' +
  ';##########################################################################\n';

export function createTrcWriter(): TraceWriter {
  let messageNumber = 0;
  return {
    header: () => TRC_HEADER,
    frame(frame: CanFrame, isRx: boolean): string {
      messageNumber += 1;
      const id = frame.extended
        ? frame.id.toString(16).toUpperCase().padStart(8, '0')
        : frame.id.toString(16).toUpperCase().padStart(3, '0');
      const number = String(messageNumber).padStart(6);
      const ms = (frame.timestamp_s * 1000).toFixed(3).padStart(12);
      const direction = (isRx ? 'Rx' : 'Tx').padEnd(3);
      return `${number})  ${ms}  ${direction} ${id.padStart(10)}  ${frame.dlc}${formatDataBytes(frame)}\n`;
    },
  };
}

export function parseTrcLine(line: string): CanFrame | null {
  if (line.length === 0 || line.startsWith(';')) return null;

  const tokens = line.trim().split(/\s+/);
  const [, msToken, , idToken, dlcToken] = tokens;
  if (msToken === undefined || idToken === undefined || dlcToken === undefined) return null;

  const timeOffsetMs = Number(msToken);
  if (!Number.isFinite(timeOffsetMs)) return null;

  const dlcValue = Number(dlcToken);
  if (!Number.isFinite(dlcValue)) return null;

  const id = parseHex(idToken);
  if (id === null) return null;

  const dlc = clampDlc(dlcValue);
  return {
    id,
    dlc,
    data: parseDataBytes(tokens.slice(5), dlc),
    extended: idToken.length > 3,
    remote: false,
    timestamp_s: timeOffsetMs / 1000,
  };
}
